import React, {
  useCallback, useEffect, useRef, useState,
} from "react";

import { MinusOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, InputNumber } from "antd";

import "./CardStepper.css";

const formatQuantity = (val, padLength = 2) => (
  val == null || val === "" ? "0".repeat(padLength) : String(val).padStart(padLength, "0")
);

const parseQuantity = (val) => {
  const digits = String(val ?? "").replace(/\D/g, "");
  return digits ? Number(digits) : 0;
};

const clampValue = (value, min, max) => Math.min(max, Math.max(min, parseQuantity(value)));

const blockNonNumericInput = (e) => {
  if (e.ctrlKey || e.metaKey || e.key.length !== 1 || /\d/.test(e.key)) return;
  e.preventDefault();
};

const moveCursorToEnd = (input) => {
  if (!input?.setSelectionRange) return;
  const length = input.value?.length ?? 0;
  input.setSelectionRange(length, length);
};

const CardStepper = ({
  value,
  min = 1,
  max = 100,
  onChange,
  className = "",
  hasError = false,
  padLength = 2,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);
  const numericValue = value ?? min;

  const getInputElement = useCallback(() => {
    if (!inputRef.current) return null;
    if (inputRef.current.input) return inputRef.current.input;
    return inputRef.current.querySelector?.("input") ?? null;
  }, []);

  const setValue = (next) => {
    if (onChange) onChange(clampValue(next, min, max));
  };

  useEffect(() => {
    if (!isFocused) return;
    requestAnimationFrame(() => moveCursorToEnd(getInputElement()));
  }, [numericValue, isFocused, getInputElement]);

  const handleFocus = (e) => {
    setIsFocused(true);
    requestAnimationFrame(() => moveCursorToEnd(e.target));
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const handleInputClick = (e) => {
    requestAnimationFrame(() => moveCursorToEnd(e.target));
  };

  const handleKeyDown = (e) => {
    blockNonNumericInput(e);
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      requestAnimationFrame(() => moveCursorToEnd(e.target));
    }
  };

  return (
    <div className={["CardStepper", className].filter(Boolean).join(" ")}>
      <Button
        type="default"
        htmlType="button"
        className="CardStepper__Button"
        disabled={numericValue <= min}
        onClick={() => setValue(numericValue - 1)}
        icon={<MinusOutlined aria-hidden />}
      />
      <InputNumber
        ref={inputRef}
        className={["CardStepper__Input", hasError ? "CardStepper__Input--error" : ""].filter(Boolean).join(" ")}
        value={numericValue}
        min={min}
        max={max}
        step={1}
        precision={0}
        controls={false}
        inputMode="numeric"
        formatter={(val) => (isFocused ? String(val ?? "") : formatQuantity(val, padLength))}
        parser={parseQuantity}
        onChange={setValue}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onClick={handleInputClick}
        onKeyDown={handleKeyDown}
        onPaste={(e) => {
          if (!/^\d+$/.test(e.clipboardData.getData("text").trim())) e.preventDefault();
        }}
      />
      <Button
        type="default"
        htmlType="button"
        className="CardStepper__Button"
        disabled={numericValue >= max}
        onClick={() => setValue(numericValue + 1)}
        icon={<PlusOutlined aria-hidden />}
      />
    </div>
  );
};

export default CardStepper;
